import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Radio, Select } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init';
import { hasPresetInferenceImage } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import { ImagePullAuthFields, hasPresetImagePullAuth, resolveImageSummaryFromContainerForm } from '@/delivery-type-plugin/components';
import {
  INFERENCE_SYSTEM_FIELD,
  MAAS_INPUT_PLACEHOLDER,
  MAAS_SELECT_PLACEHOLDER,
  MASTER_SYSTEM_DISK_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
  buildImageItemFromInferenceSystem,
  formJsonFieldFormatter,
  formJsonFieldNormalize,
  maasRequiredRules,
  parseFormJsonField,
  parseInferenceFrameworkImageItem,
  resolveFrameworkFromInferenceImage,
} from '@/delivery-type-plugin/plugins';
import styles from '@/delivery-type-plugin/styles/maas-model-step.module.less';

import type { ApplicationInstanceFormValues } from '../types';
import { AppImageResourceSelectField } from './app-image-resource-select-field';
import { useApplicationImageSetting } from './useApplicationImageSetting';

const FormItem = Form.Item;

export interface ApplicationImageSettingProps {
  form: FormInstance;
  compact?: boolean;
  isEdit?: boolean;
  initValues?: ApplicationInstanceFormValues;
  /** SKU 规格创建：按第一步所选架构过滤镜像 */
  presetArchitecture?: string;
  /** SKU presetConfig 已含镜像时锁定镜像选择器（下单场景） */
  presetImageLocked?: boolean;
  /** 下单：SKU 预置已有镜像时隐藏删/改 */
  lockActionsWhenFilled?: boolean;
}

/** 应用镜像：写入 _inferenceSystem / _inferenceService，提交时再合并入主组 */
export function ApplicationImageSetting({
  form,
  compact,
  isEdit,
  initValues,
  presetArchitecture,
  presetImageLocked,
  lockActionsWhenFilled,
}: ApplicationImageSettingProps) {
  const { t } = useTranslation();
  const runMode = Form.useWatch('runMode', form) as MaasRunMode | undefined;
  const systemField = INFERENCE_SYSTEM_FIELD;
  const systemDiskField = runMode === MaasRunMode.PrimarySecondary ? MASTER_SYSTEM_DISK_FIELD : WORKER_SYSTEM_DISK_FIELD;
  const imageQueryObj = useMemo(() => (presetArchitecture ? { cpu: presetArchitecture } : {}), [presetArchitecture]);
  const imagePaths = useMemo(
    () => ({
      imageKey: `${systemField}.imageKey`,
      imageUrl: `${systemField}.imageName`,
      imagePrefix: systemField,
    }),
    [systemField],
  );
  const watchedImageKey = Form.useWatch(imagePaths.imageKey, form);
  const watchedImageUrl = Form.useWatch(imagePaths.imageUrl, form);
  const inferenceSystemRaw = Form.useWatch(INFERENCE_SYSTEM_FIELD, form);
  const inferenceFrameworkImageRaw = Form.useWatch('inferenceFrameworkImage', form);

  const { imageMappings, imageSource, handleSourceChange, handleImageChange, handleImageClear, presetImageItem } =
    useApplicationImageSetting({
      form,
      isEdit,
      initValues,
      systemField,
      systemDiskField,
      presetArchitecture,
      presetImageLocked,
    });

  /** 仅 SKU 预置已有镜像时配合 lockActionsWhenFilled 锁删改 */
  const presetHasImage = Boolean(initValues && hasPresetInferenceImage(initValues as Record<string, unknown>));
  const imageLocked = Boolean(presetImageLocked) || (Boolean(lockActionsWhenFilled) && presetHasImage);
  /** 镜像拉取认证与镜像锁定解耦：仅 SKU 预置了账密/认证开关时禁用 */
  const pullAuthLocked = hasPresetImagePullAuth(initValues as Record<string, unknown> | undefined);

  const imageSourceOptions = useMemo(
    () => [
      { label: t('data-center:maas.app.image.source.builtin'), value: MaasInferenceSource.Builtin },
      { label: t('data-center:maas.app.image.source.custom'), value: MaasInferenceSource.Custom },
    ],
    [t],
  );

  const imageSummarySource = useMemo(() => {
    const flatImageEchoKey = [watchedImageKey, watchedImageUrl].join('|');
    void flatImageEchoKey;

    if (imageLocked && presetImageItem) {
      return {
        displayName: presetImageItem.name,
        os: presetImageItem.vendor,
        version: presetImageItem.version,
        architecture: presetImageItem.cpuArchitectures?.[0],
        systemDiskSize: presetImageItem.options?.systemImage?.systemVolumeSize,
      };
    }
    const fromForm = resolveImageSummaryFromContainerForm(form.getFieldsValue() as Record<string, unknown>, imagePaths, presetArchitecture);
    if (fromForm) return fromForm;

    const system = parseFormJsonField<Record<string, unknown>>(inferenceSystemRaw);
    if (!system) return null;
    const catalogItem = parseInferenceFrameworkImageItem(inferenceFrameworkImageRaw) || buildImageItemFromInferenceSystem(system);
    if (!catalogItem) return null;

    return {
      displayName: catalogItem.name,
      os: catalogItem.vendor,
      version: catalogItem.version,
      architecture: catalogItem.cpuArchitectures?.[0] || String(system.architecture ?? '').trim() || undefined,
      systemDiskSize: catalogItem.options?.systemImage?.systemVolumeSize ?? system.minSystemDiskGi,
    };
  }, [
    form,
    imagePaths,
    inferenceFrameworkImageRaw,
    inferenceSystemRaw,
    imageLocked,
    presetArchitecture,
    presetImageItem,
    watchedImageKey,
    watchedImageUrl,
  ]);

  return (
    <>
      <FormItem field="framework" hidden>
        <Input />
      </FormItem>
      <FormItem
        field={INFERENCE_SYSTEM_FIELD}
        hidden
        style={{ display: 'none', margin: 0 }}
        triggerPropName="value"
        formatter={formJsonFieldFormatter}
        normalize={formJsonFieldNormalize}
      >
        <Input />
      </FormItem>
      {/** 须挂真实 field：编辑旧 preset 无 maasInferenceSource 时由 hook 回写，避免确认保存落到笼统「请填写必填项」 */}
      <FormItem
        label={t('data-center:image.source')}
        field={MAAS_INFERENCE_SOURCE_FIELD}
        required
        rules={imageLocked ? undefined : [{ required: true, message: t('common:required.field') }]}
        layout="horizontal"
        labelAlign="left"
        labelCol={{ flex: 'none' }}
        wrapperCol={{ flex: 'none' }}
        className={styles.deployModeRow}
        disabled={imageLocked}
      >
        {compact ? (
          <Radio.Group options={imageSourceOptions} type="button" size="small" onChange={handleSourceChange} />
        ) : (
          <Select options={imageSourceOptions} placeholder={MAAS_SELECT_PLACEHOLDER} onChange={handleSourceChange} />
        )}
      </FormItem>

      {imageSource === MaasInferenceSource.Builtin ? (
        <>
          <AppImageResourceSelectField
            form={form}
            queryObj={imageQueryObj}
            imageKeyField={imagePaths.imageKey}
            imageUrlField={imagePaths.imageUrl}
            imagePrefix={imagePaths.imagePrefix}
            readOnly={imageLocked}
            lockActionsWhenFilled={lockActionsWhenFilled}
            presetHasImage={presetHasImage}
            summarySource={imageSummarySource}
            onImageChange={handleImageChange}
            onImageClear={handleImageClear}
          />
          {imageMappings.map((mapping) => (
            <FormItem key={mapping.field} field={mapping.field} hidden>
              <Input />
            </FormItem>
          ))}
          <FormItem field="inferenceFrameworkImage" hidden>
            <Input />
          </FormItem>
        </>
      ) : (
        <FormItem
          label={t('data-center:maas.app.image.custom')}
          field={`${systemField}.image`}
          rules={imageLocked ? undefined : maasRequiredRules(t('data-center:maas.app.image.custom'))}
        >
          <Input
            placeholder={MAAS_INPUT_PLACEHOLDER}
            readOnly={imageLocked}
            disabled={imageLocked}
            onBlur={() => {
              if (imageLocked) return;
              const system = form.getFieldValue(systemField) as Record<string, unknown> | undefined;
              form.setFieldValue('framework', resolveFrameworkFromInferenceImage(null, system));
            }}
          />
        </FormItem>
      )}
      <ImagePullAuthFields form={form} disabled={pullAuthLocked} />
    </>
  );
}

export default ApplicationImageSetting;
